import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  KeyRound, ScanSearch, UploadCloud, Globe, Check, Loader2, ArrowRight, ArrowLeft,
  FileText, Search, X, Rocket, Image as ImageIcon,
} from "lucide-react";
import { ModelPicker } from "@/components/ModelPicker";
import { estimateCost, fmtUSD } from "@/lib/format";

const STEPS = [
  { id: 1, title: "API keys", icon: KeyRound },
  { id: 2, title: "Corrector", icon: ScanSearch },
  { id: 3, title: "Manuscript", icon: UploadCloud },
  { id: 4, title: "Languages", icon: Globe },
];

const PRESETS = [
  { name: "Top EU", codes: ["de", "fr", "es", "it", "nl", "pl", "pt"] },
  { name: "Global Big 5", codes: ["es", "zh", "hi", "ar", "fr"] },
  { name: "Nordics", codes: ["sv", "da", "fi", "nb", "no"] },
  { name: "Asia", codes: ["zh", "ja", "ko", "hi", "id", "th"] },
];

const DOMAINS = ["", "literary", "general", "marketing", "technical", "legal", "medical", "academic"];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [languages, setLanguages] = useState([]);
  const [models, setModels] = useState([]);
  const [busy, setBusy] = useState(false);

  const [raviraKey, setRaviraKey] = useState("");
  const [orKey, setOrKey] = useState("");
  const [hasRavira, setHasRavira] = useState(false);
  const [hasOr, setHasOr] = useState(false);

  const [model, setModel] = useState("google/gemini-2.5-flash");
  const [meta, setMeta] = useState({ title: "", author: "", source_lang: "auto", domain: "" });
  const [file, setFile] = useState(null);
  const [cover, setCover] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [targets, setTargets] = useState([]);
  const [search, setSearch] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [l, s] = await Promise.all([api.get("/languages"), api.get("/settings")]);
        setLanguages(l.data.languages);
        setHasRavira(s.data.has_ravira);
        setHasOr(s.data.has_openrouter);
        if (s.data.corrector_model) setModel(s.data.corrector_model);
      } catch (e) { toast.error("Failed to load setup data"); }
    })();
  }, []);

  const loadModels = async () => {
    try { const { data } = await api.get("/corrector/models"); setModels(data.models); }
    catch (e) { setModels([]); }
  };

  const filteredLangs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return languages.filter((l) =>
      !q || l.name.toLowerCase().includes(q) || l.code.includes(q) || (l.native || "").toLowerCase().includes(q));
  }, [languages, search]);

  const codeSet = new Set(languages.map((l) => l.code));

  const toggleLang = (code) =>
    setTargets((t) => (t.includes(code) ? t.filter((c) => c !== code) : [...t, code]));

  const applyPreset = (codes) => {
    const valid = codes.filter((c) => codeSet.has(c));
    setTargets((t) => Array.from(new Set([...t, ...valid])));
    toast.success(`Added ${valid.length} languages`);
  };

  const saveKeys = async () => {
    const payload = {};
    if (raviraKey.trim()) payload.ravira_key = raviraKey.trim();
    if (orKey.trim()) payload.openrouter_key = orKey.trim();
    if (Object.keys(payload).length) {
      await api.put("/settings/keys", payload);
      if (payload.ravira_key) setHasRavira(true);
      if (payload.openrouter_key) setHasOr(true);
      toast.success("Keys saved securely");
    }
  };

  const next = async () => {
    if (step === 1) { setBusy(true); try { await saveKeys(); await loadModels(); } finally { setBusy(false); } }
    if (step === 2) { await api.put("/settings/keys", { corrector_model: model }); }
    if (step === 3) {
      if (!meta.title || !meta.author) return toast.error("Add a title and author");
      if (!file) return toast.error("Upload a manuscript file");
    }
    setStep((s) => Math.min(4, s + 1));
  };

  const onCover = (f) => {
    setCover(f);
    if (f) setCoverPreview(URL.createObjectURL(f));
  };

  const onFile = async (f) => {
    setFile(f);
    setEstimate(null);
    if (!f) return;
    setEstimating(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/estimate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEstimate(data);
    } catch (e) { /* estimate is best-effort */ }
    finally { setEstimating(false); }
  };

  const selectedModel = models.find((m) => m.id === model);
  const cost = estimate ? estimateCost(estimate.char_count, selectedModel, targets.length || 1) : null;

  const launch = async () => {
    if (targets.length === 0) return toast.error("Select at least one target language");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", meta.title);
      fd.append("author", meta.author);
      fd.append("source_lang", meta.source_lang);
      fd.append("domain", meta.domain);
      fd.append("target_langs", JSON.stringify(targets));
      fd.append("corrector_model", model);
      fd.append("file", file);
      if (cover) fd.append("cover", cover);
      const { data } = await api.post("/projects", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const pid = data.project.id;
      await api.post(`/projects/${pid}/run`);
      toast.success("Engine started — translating your catalog");
      navigate(`/project/${pid}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start project");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#f6f5f1]">
      <Header />
      <main className="mx-auto max-w-4xl px-5 py-10 sm:px-8">
        {/* Stepper */}
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex flex-1 items-center">
              <div className="flex items-center gap-2.5">
                <div className={`flex h-10 w-10 items-center justify-center rounded-xl border transition-all ${
                  step >= s.id ? "border-[#0f172a] bg-[#0f172a] text-white" : "border-[#e6e4dd] bg-white text-[#94a3b8]"}`}>
                  {step > s.id ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                </div>
                <div className="hidden sm:block">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#94a3b8]">Step {s.id}</div>
                  <div className="text-sm font-medium">{s.title}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div className={`mx-3 h-px flex-1 ${step > s.id ? "bg-[#0f172a]" : "bg-[#e6e4dd]"}`} />}
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-3xl border border-[#e6e4dd] bg-white p-6 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>

              {step === 1 && (
                <div>
                  <h2 className="font-display text-2xl tracking-tight">Connect your keys</h2>
                  <p className="mt-2 text-sm text-[#475569]">Keys are encrypted at rest and used only for your translations. Leave blank to run a labelled simulation.</p>
                  <div className="mt-6 space-y-5">
                    <div className="space-y-1.5">
                      <Label>RAVIRA API key {hasRavira && <span className="text-[#059669]">· saved</span>}</Label>
                      <Input data-testid="ravira-key-input" type="password" value={raviraKey}
                        onChange={(e) => setRaviraKey(e.target.value)}
                        placeholder={hasRavira ? "•••••• (already saved)" : "rk_live_..."} className="h-11 rounded-xl" />
                      <p className="text-xs text-[#94a3b8]">Get one at ravira.dev · used for chapter-by-chapter translation.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label>OpenRouter API key {hasOr && <span className="text-[#059669]">· saved</span>}</Label>
                      <Input data-testid="openrouter-key-input" type="password" value={orKey}
                        onChange={(e) => setOrKey(e.target.value)}
                        placeholder={hasOr ? "•••••• (already saved)" : "sk-or-..."} className="h-11 rounded-xl" />
                      <p className="text-xs text-[#94a3b8]">Powers the intent corrector (Gemini, Claude, GPT, Llama…).</p>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <h2 className="font-display text-2xl tracking-tight">Choose your intent corrector</h2>
                  <p className="mt-2 text-sm text-[#475569]">This model compares every translated passage to your original — it flags, it never rewrites your style. Search {models.length || "many"} OpenRouter models; live prices shown when your key is saved.</p>
                  <div className="mt-6">
                    <ModelPicker models={models} value={model} onChange={setModel} testId="onboarding-model-picker" />
                  </div>
                  {selectedModel && (
                    <div className="mt-3 rounded-xl border border-[#e6e4dd] bg-[#f6f5f1] p-4 text-sm">
                      <div className="font-medium">{selectedModel.name || selectedModel.id}</div>
                      <div className="mt-1 text-xs text-[#94a3b8] font-mono2">{selectedModel.id}</div>
                    </div>
                  )}
                </div>
              )}

              {step === 3 && (
                <div>
                  <h2 className="font-display text-2xl tracking-tight">Upload your manuscript</h2>
                  <p className="mt-2 text-sm text-[#475569]">.docx, .txt or .epub — chapters and structure are preserved.</p>
                  <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Book title</Label>
                      <Input data-testid="title-input" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                        placeholder="The Silent Compass" className="h-11 rounded-xl" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Author</Label>
                      <Input data-testid="author-input" value={meta.author} onChange={(e) => setMeta({ ...meta, author: e.target.value })}
                        placeholder="Jane Author" className="h-11 rounded-xl" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Source language</Label>
                      <Select value={meta.source_lang} onValueChange={(v) => setMeta({ ...meta, source_lang: v })}>
                        <SelectTrigger data-testid="source-lang-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-64 bg-white">
                          <SelectItem value="auto">Auto detect</SelectItem>
                          {languages.map((l) => <SelectItem key={l.code} value={l.code}>{l.flag} {l.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Domain (optional)</Label>
                      <Select value={meta.domain} onValueChange={(v) => setMeta({ ...meta, domain: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="domain-select" className="h-11 rounded-xl"><SelectValue placeholder="General" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {DOMAINS.map((d) => <SelectItem key={d || "none"} value={d || "none"}>{d ? d[0].toUpperCase() + d.slice(1) : "General"}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <label data-testid="manuscript-dropzone" className="mt-5 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#d6d3ca] bg-[#f6f5f1] px-6 py-8 text-center transition-colors hover:border-[#0f172a]">
                    <input type="file" accept=".txt,.md,.docx,.epub" className="hidden"
                      onChange={(e) => onFile(e.target.files[0])} />
                    {file ? (
                      <div className="flex items-center gap-2 text-[#0f172a]"><FileText className="h-5 w-5" /> {file.name}</div>
                    ) : (
                      <><UploadCloud className="h-7 w-7 text-[#94a3b8]" /><span className="mt-2 text-sm text-[#475569]">Click to upload manuscript</span></>
                    )}
                    {(estimating || estimate) && (
                      <span className="mt-2 text-xs text-[#94a3b8] font-mono2" data-testid="estimate-summary">
                        {estimating ? "Analysing…" : `${estimate.char_count.toLocaleString()} characters · ${estimate.chapter_count} chapters · ${estimate.passage_count} passages`}
                      </span>
                    )}
                  </label>

                  <div className="mt-5">
                    <Label className="mb-1.5 block">Cover (optional — auto-generated if skipped)</Label>
                    <div className="flex items-center gap-4">
                      <label data-testid="cover-upload" className="flex cursor-pointer items-center gap-2 rounded-xl border border-[#e6e4dd] bg-white px-4 py-2.5 text-sm hover:bg-slate-900 hover:text-white">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => onCover(e.target.files[0])} />
                        <ImageIcon className="h-4 w-4" /> {cover ? "Change cover" : "Upload cover"}
                      </label>
                      {coverPreview && <img src={coverPreview} alt="cover" className="h-16 w-12 rounded-md object-cover shadow" />}
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div>
                  <h2 className="font-display text-2xl tracking-tight">Pick your markets</h2>
                  <p className="mt-2 text-sm text-[#475569]">Every language becomes its own verified EPUB. Selected: <span className="font-semibold text-[#0f172a]">{targets.length}</span></p>

                  {estimate && (
                    <div data-testid="cost-estimate" className="mt-4 rounded-2xl border border-[#e6e4dd] bg-[#0f172a] p-5 text-white">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-mono2">Estimated corrector cost</div>
                          <div className="font-display mt-1 text-3xl">
                            {cost ? fmtUSD(cost.total) : "—"}
                            <span className="ml-2 text-sm text-slate-400">for {targets.length || 1} language{(targets.length || 1) > 1 ? "s" : ""}</span>
                          </div>
                        </div>
                        <div className="text-right text-xs text-slate-300 font-mono2">
                          <div>{estimate.char_count.toLocaleString()} chars</div>
                          {cost && <div>≈{fmtUSD(cost.perLang)}/language</div>}
                        </div>
                      </div>
                      <p className="mt-3 text-xs text-slate-400">
                        {cost
                          ? `Based on ${selectedModel?.name || model} pricing. RAVIRA translation is billed separately per character on your RAVIRA account.`
                          : "Save an OpenRouter key with pricing to see a $ estimate. RAVIRA translation is billed per character on your RAVIRA account."}
                      </p>
                    </div>
                  )}

                  <div className="mt-5 flex flex-wrap gap-2">
                    {PRESETS.map((p) => (
                      <button key={p.name} data-testid={`preset-${p.name}`} onClick={() => applyPreset(p.codes)}
                        className="rounded-full border border-[#e6e4dd] bg-white px-3.5 py-1.5 text-xs font-medium hover:bg-slate-900 hover:text-white">
                        + {p.name}
                      </button>
                    ))}
                    {targets.length > 0 && (
                      <button data-testid="clear-langs" onClick={() => setTargets([])}
                        className="rounded-full border border-rose-200 bg-white px-3.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-600 hover:text-white">
                        Clear all
                      </button>
                    )}
                  </div>

                  <div className="relative mt-4">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94a3b8]" />
                    <Input data-testid="lang-search" value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search 106 languages…" className="h-11 rounded-xl pl-9" />
                  </div>

                  {targets.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {targets.map((c) => {
                        const l = languages.find((x) => x.code === c);
                        return (
                          <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-[#0f172a] px-3 py-1 text-xs text-white">
                            {l?.flag} {l?.name || c}
                            <button onClick={() => toggleLang(c)}><X className="h-3 w-3" /></button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-4 grid max-h-72 grid-cols-2 gap-2 overflow-y-auto rounded-xl border border-[#e6e4dd] p-3 sm:grid-cols-3">
                    {filteredLangs.map((l) => {
                      const active = targets.includes(l.code);
                      return (
                        <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => toggleLang(l.code)}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-all ${
                            active ? "border-[#0f172a] bg-[#0f172a] text-white" : "border-transparent bg-[#f6f5f1] hover:border-[#e6e4dd]"}`}>
                          <span>{l.flag}</span>
                          <span className="truncate">{l.name}</span>
                          {active && <Check className="ml-auto h-3.5 w-3.5" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Nav */}
          <div className="mt-8 flex items-center justify-between border-t border-[#e6e4dd] pt-5">
            <Button variant="ghost" disabled={step === 1} onClick={() => setStep((s) => s - 1)}
              data-testid="wizard-back-btn" className="text-[#475569] hover:bg-slate-900 hover:text-white disabled:opacity-40">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
            </Button>
            {step < 4 ? (
              <Button data-testid="wizard-next-btn" onClick={next} disabled={busy}
                className="h-11 rounded-full bg-[#0f172a] px-6 text-white hover:bg-[#1e293b]">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="ml-1.5 h-4 w-4" /></>}
              </Button>
            ) : (
              <Button data-testid="launch-engine-btn" onClick={launch} disabled={busy}
                className="h-11 rounded-full bg-[#d97706] px-6 text-white hover:bg-[#b45309]">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Rocket className="mr-1.5 h-4 w-4" /> Launch engine</>}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
